import { getLandingContent, type VerticalLandingContent } from '@nexofacil/schemas';
import {
  ArrowLeft,
  ArrowRight,
  Bath,
  BedDouble,
  Building2,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  Expand,
  MapPin,
  Ruler,
  HardHat,
  KeyRound,
  Sparkles,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import YarlLightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type GetPropertyResponse } from '@/lib/api';
import { brokerAvatarClass } from '@/lib/broker-avatar';
import { applyBrokerTheme } from '@/lib/broker-theme';
import { useDocumentTitle } from '@/lib/use-document-title';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingState } from '@/components/ui/loading-state';
import { formatCents } from '@/lib/format';
import { propertyTypeLabel } from '@/lib/property-display';
import { propertyStageLabel } from '@/lib/labels';
import { detectSubdomain } from '@/lib/subdomain';
import { cn } from '@/lib/utils';
import { messages } from '@/messages';
import { SeoHead } from '@/components/seo-head';

export function PropertyDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<GetPropertyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(
    data?.property && data?.broker
      ? `${data.property.name} · ${data.broker.name} · NexoFácil`
      : undefined,
  );

  const seo = data
    ? {
        title: `${data.property.name} · ${data.broker.name} · NexoFácil`,
        description: data.property.description
          ? data.property.description.slice(0, 220)
          : `${data.property.name} em ${data.property.city}/${data.property.state}. Análise gratuita do seu perfil MCMV em 2 minutos.`,
        ogImage: data.property.photos[0] ?? data.broker.avatarUrl ?? undefined,
        canonical:
          typeof window !== 'undefined'
            ? `${window.location.origin}${window.location.pathname}`
            : undefined,
      }
    : null;

  useEffect(() => {
    const sub = detectSubdomain();
    if (!sub || !slug) {
      setError(messages.errorNotFound);
      return;
    }
    api
      .getProperty(sub, slug)
      .then((res) => {
        const content = getLandingContent(res.broker.verticalKey);
        applyBrokerTheme(content.defaultTheme, res.broker.theme);
        setData(res);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) {
          setError(messages.errorNotFound);
        } else if (e instanceof Error) {
          setError(e.message);
        } else {
          setError(messages.errorGeneric);
        }
      });
  }, [slug]);

  if (error) {
    const isPropertyNotFound =
      error === messages.errorNotFound || error === messages.errorBrokerNotFound;
    return (
      <PublicShell>
        <EmptyState
          icon={isPropertyNotFound ? Building2 : TriangleAlert}
          tone={isPropertyNotFound ? 'default' : 'error'}
          size="lg"
          title={
            isPropertyNotFound
              ? messages.emptyPropertyNotFoundTitle
              : messages.emptyGenericTitle
          }
          description={
            isPropertyNotFound
              ? messages.emptyPropertyNotFoundBody
              : messages.emptyGenericBody
          }
          action={{ label: messages.ctaSeeProperties, to: '/' }}
          secondaryAction={{
            label: messages.ctaTryAgain,
            onClick: () => window.location.reload(),
          }}
        />
      </PublicShell>
    );
  }

  if (!data) {
    return (
      // Sem marca NexoFácil: quem chega pelo anúncio do corretor não vê outra marca primeiro.
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState size="lg" />
      </div>
    );
  }

  const { broker, property } = data;
  const content = getLandingContent(broker.verticalKey);
  const goQualify = () =>
    navigate(`/empreendimento/${property.slug}/qualificar`);

  return (
    <div className="min-h-screen bg-background">
      {seo && (
        <SeoHead
          title={seo.title}
          description={seo.description}
          ogImage={seo.ogImage}
          canonical={seo.canonical}
        />
      )}
      <DetailHeader broker={broker} content={content} onQualify={goQualify} />

      <main className="pb-32 md:pb-20">
        <div className="container py-6">
          <button
            type="button"
            // Veio da lista: volta pro mesmo ponto da rolagem. Link direto: vai pra landing.
            onClick={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/'))}
            className="-my-3 inline-flex items-center gap-1.5 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {content.backToListings}
          </button>
        </div>

        <PhotoGallery photos={property.photos} alt={property.name} />

        <div className="container mt-8 grid gap-10 md:mt-12 md:grid-cols-[1fr_360px] md:gap-12">
          <article className="space-y-12">
            <PropertyHeader property={property} content={content} />

            {property.kind === 'listing' && property.specs && <SpecsSection specs={property.specs} />}

            {property.description && (
              <DescriptionSection
                text={property.description}
                title={property.kind === 'listing' ? 'Sobre o imóvel' : messages.propertyAboutTitle}
              />
            )}

            {property.unitTypes.length > 0 && (
              <UnitTypesSection unitTypes={property.unitTypes} />
            )}

            {property.features.length > 0 && (
              <FeaturesSection features={property.features} />
            )}
          </article>

          <CtaCard
            property={property}
            broker={broker}
            content={content}
            onQualify={goQualify}
          />
        </div>
      </main>

      <MobileStickyCta property={property} content={content} onQualify={goQualify} />
    </div>
  );
}

// ============================================================================
// Sections
// ============================================================================

function DetailHeader({
  broker,
  content,
  onQualify,
}: {
  broker: GetPropertyResponse['broker'];
  content: VerticalLandingContent;
  onQualify: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-primary-foreground/10 bg-primary">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-3">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt=""
              className={brokerAvatarClass(broker.avatarShape ?? undefined, 'h-9 w-9 ring-2 ring-primary-foreground/30')}
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-sm font-semibold text-primary-foreground">
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-sm font-semibold text-primary-foreground">{broker.name}</div>
            <div className="text-xs text-primary-foreground/75">{content.brokerSubtitle}</div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={onQualify}
          className="h-11 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
        >
          {content.brokerHeaderCta}
        </Button>
      </div>
    </header>
  );
}

function PhotoGallery({ photos, alt }: { photos: string[]; alt: string }) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const thumbsRef = useRef<HTMLDivElement>(null);

  const go = useCallback(
    (delta: number) => {
      setActive((cur) => (cur + delta + photos.length) % photos.length);
    },
    [photos.length],
  );

  // Keep the active thumbnail in view as the user navigates.
  useEffect(() => {
    const node = thumbsRef.current?.querySelector<HTMLButtonElement>(
      `[data-thumb-index="${active}"]`,
    );
    node?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [active]);

  if (photos.length === 0) {
    return (
      <div className="container">
        <div className="flex aspect-[16/9] w-full items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
          Sem fotos disponíveis
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Main image · no celular, 4:3 cobrindo (sem tarja preta empurrando o
          nome pra baixo da dobra). No desktop, object-contain num palco de
          altura fixa pra fachada vertical e planta não serem cortadas. */}
      <div className="group relative overflow-hidden rounded-2xl bg-neutral-900 shadow-sm">
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="block aspect-[4/3] w-full focus:outline-none md:aspect-auto md:h-[min(70vh,600px)]"
          aria-label={`Abrir foto ${active + 1} em tela cheia`}
        >
          <img
            src={photos[active]}
            alt={`${alt} · foto ${active + 1}`}
            className="h-full w-full object-cover md:object-contain"
          />
        </button>

        {photos.length > 1 && (
          <>
            <GalleryArrow direction="prev" onClick={() => go(-1)} />
            <GalleryArrow direction="next" onClick={() => go(1)} />
          </>
        )}

        <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white backdrop-blur">
          {active + 1} / {photos.length}
        </div>

        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white transition-opacity hover:bg-black/80 focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"
          aria-label="Ampliar"
        >
          <Expand className="h-4 w-4" />
        </button>
      </div>

      {photos.length > 1 && (
        <div
          ref={thumbsRef}
          className="mt-3 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2 [scrollbar-width:thin]"
        >
          {photos.map((p, i) => (
            <button
              key={p}
              type="button"
              data-thumb-index={i}
              onClick={() => setActive(i)}
              className={cn(
                'relative h-16 w-24 shrink-0 snap-start overflow-hidden rounded-lg bg-neutral-100 ring-offset-background transition-all md:h-20 md:w-32',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                i === active
                  ? 'ring-2 ring-primary ring-offset-2'
                  : 'opacity-60 hover:opacity-100',
              )}
              aria-label={`Foto ${i + 1}`}
              aria-current={i === active}
            >
              <img src={p} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <YarlLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={active}
        on={{ view: ({ index }) => setActive(index) }}
        slides={photos.map((src) => ({ src, alt }))}
        plugins={[Counter, Fullscreen, Thumbnails, Zoom]}
        counter={{ container: { style: { top: 0, bottom: 'unset' } } }}
        thumbnails={{ position: 'bottom', width: 96, height: 64, gap: 8 }}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
        carousel={{ finite: false, padding: '16px' }}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, .95)' } }}
        controller={{ closeOnBackdropClick: true }}
      />
    </div>
  );
}

function GalleryArrow({
  direction,
  onClick,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
}) {
  const isPrev = direction === 'prev';
  const Icon = isPrev ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'absolute top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg backdrop-blur transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:flex',
        isPrev ? 'left-3' : 'right-3',
      )}
      aria-label={isPrev ? 'Foto anterior' : 'Próxima foto'}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

function PropertyHeader({
  property,
  content,
}: {
  property: GetPropertyResponse['property'];
  content: VerticalLandingContent;
}) {
  return (
    <header>
      <Badge variant="warm" className="mb-3">
        {property.stage === 'ready' ? (
          <KeyRound className="h-3.5 w-3.5" />
        ) : property.stage === 'launch' ? (
          <HardHat className="h-3.5 w-3.5" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {property.stage ? propertyStageLabel[property.stage] : content.listingBadge}
      </Badge>
      <h1 className="text-balance text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
        {property.name}
      </h1>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-4 w-4" />
          {property.neighborhood ? `${property.neighborhood}, ` : ''}
          {property.city} · {property.state}
        </span>
        {property.propertyType && <span>{propertyTypeLabel[property.propertyType]}</span>}
        {property.developer && (
          <span className="hidden md:inline">por {property.developer}</span>
        )}
        {property.referenceCode && <span>Cód. {property.referenceCode}</span>}
      </div>
    </header>
  );
}

function DescriptionSection({ text, title }: { text: string; title: string }) {
  return (
    <section>
      <SectionTitle>{title}</SectionTitle>
      <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-muted-foreground">
        {text}
      </p>
    </section>
  );
}

function UnitTypesSection({
  unitTypes,
}: {
  unitTypes: GetPropertyResponse['property']['unitTypes'];
}) {
  return (
    <section>
      <SectionTitle>{messages.propertyUnitTypesTitle}</SectionTitle>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {unitTypes.map((u, i) => (
          <div
            key={i}
            className="rounded-xl border bg-card p-5 transition-shadow hover:shadow-sm"
          >
            <div className="text-base font-bold text-foreground">{u.label}</div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
              <SpecItem
                icon={BedDouble}
                value={`${u.bedrooms} ${messages.propertyBedroomsShort}`}
              />
              <SpecItem
                icon={Bath}
                value={`${u.bathrooms} ${messages.propertyBathroomsShort}`}
              />
              {u.areaSqm !== undefined && (
                <SpecItem
                  icon={Ruler}
                  value={`${u.areaSqm} ${messages.propertyAreaShort}`}
                />
              )}
              {u.parking !== undefined && u.parking > 0 && (
                <SpecItem
                  icon={Car}
                  value={`${u.parking} ${messages.propertyParkingShort}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SpecsSection({ specs }: { specs: NonNullable<GetPropertyResponse['property']['specs']> }) {
  const items: [LucideIcon, number | undefined, string, string][] = [
    [BedDouble, specs.bedrooms, 'quarto', 'quartos'],
    [Bath, specs.bathrooms, 'banheiro', 'banheiros'],
    [Car, specs.parking, 'vaga', 'vagas'],
  ];
  if (!items.some(([, v]) => v) && !specs.areaSqm) return null;
  return (
    <section>
      <SectionTitle>Características</SectionTitle>
      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 text-base text-foreground">
        {items.map(([icon, v, one, many]) =>
          v ? <SpecItem key={one} icon={icon} value={`${v} ${v === 1 ? one : many}`} /> : null,
        )}
        {specs.areaSqm && (
          <SpecItem icon={Ruler} value={`${specs.areaSqm.toLocaleString('pt-BR')} m²`} />
        )}
      </div>
    </section>
  );
}

function SpecItem({ icon: Icon, value }: { icon: LucideIcon; value: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-4 w-4 text-primary" />
      {value}
    </span>
  );
}

function FeaturesSection({ features }: { features: string[] }) {
  return (
    <section>
      <SectionTitle>{messages.propertyFeaturesTitle}</SectionTitle>
      <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 rounded-lg bg-secondary/60 px-3.5 py-2.5 text-sm"
          >
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CtaCard({
  property,
  broker,
  content,
  onQualify,
}: {
  property: GetPropertyResponse['property'];
  broker: GetPropertyResponse['broker'];
  content: VerticalLandingContent;
  onQualify: () => void;
}) {
  return (
    <aside className="md:sticky md:top-24 md:self-start">
      <div className="rounded-2xl border bg-card p-6 shadow-sm md:p-7">
        {property.priceRange ? (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {isExactPrice(property) ? 'Preço' : messages.propertyPriceFromLabel}
            </div>
            <div className="mt-1 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
              {formatCents(property.priceRange.min)}
            </div>
            {property.priceRange.max > property.priceRange.min && (
              <div className="mt-1 text-sm text-muted-foreground">
                até {formatCents(property.priceRange.max)}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Preço
            </div>
            <div className="mt-1 text-2xl font-extrabold tracking-tight text-foreground md:text-3xl">
              Consulte
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {content.listingNoPriceHint}
            </div>
          </>
        )}

        <div className="my-6 h-px bg-border" />

        <div className="flex items-center gap-3">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt={broker.name}
              className={brokerAvatarClass(broker.avatarShape ?? undefined, 'h-11 w-11')}
            />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-base font-bold text-primary">
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-xs text-muted-foreground">{content.brokerCardLabel}</div>
            <div className="text-sm font-semibold text-foreground">{broker.name}</div>
          </div>
        </div>

        <Button
          size="lg"
          onClick={onQualify}
          className="mt-6 h-12 w-full text-base font-semibold"
        >
          {content.listingCta}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          {content.listingCtaHint}
        </p>
      </div>
    </aside>
  );
}

function MobileStickyCta({
  property,
  content,
  onQualify,
}: {
  property: GetPropertyResponse['property'];
  content: VerticalLandingContent;
  onQualify: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 backdrop-blur md:hidden">
      <div className="container flex items-center justify-between gap-3 py-3">
        <div className="leading-tight">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {property.priceRange && !isExactPrice(property) ? messages.propertyPriceFromLabel : 'Preço'}
          </div>
          <div className="text-base font-bold">
            {property.priceRange ? formatCents(property.priceRange.min) : 'Consulte'}
          </div>
        </div>
        <Button
          size="lg"
          onClick={onQualify}
          className="h-12 flex-1 text-sm font-semibold"
        >
          {content.listingCta}
        </Button>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-2xl font-bold tracking-tight md:text-[1.75rem]">
      {children}
    </h2>
  );
}

/** Minimal NexoFácil-branded shell used for error/loading states. */
function PublicShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b bg-background">
        <div className="container flex h-16 items-center gap-2">
          <img src="/nexofacil-icon.png" alt="" className="h-7 w-7" />
          <span className="text-sm font-semibold">NexoFácil</span>
        </div>
      </header>
      <main className="container flex flex-1 items-center justify-center">
        {children}
      </main>
      <footer className="border-t bg-background">
        <div className="container py-6 text-center text-xs text-muted-foreground">
          NexoFácil · {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

/** Imóvel avulso tem preço fechado · empreendimento mostra "a partir de". */
function isExactPrice(p: GetPropertyResponse['property']): boolean {
  return p.kind === 'listing';
}
